// master.ts — 원가 마스터 데이터(설비/공정/임율/비중/상수).
//
// 백엔드 `apps/api/api/core/cost/master.py` 와 동일한 reference 테이블 + lookup 헬퍼.
// 한쪽을 수정하면 반드시 양쪽 + 테스트를 함께 수정해야 한다(CLAUDE.md).
//
// Excel 출처:
//   - 설비 마스터 : 직접경비_내부!C22:I33
//   - 임율        : 직접경비_내부!C35:D46
//   - 비중        : CNC1!AH3:AJ15
//   - 원가 상수   : CNC1!D56:M57

export interface Equipment {
  name: string; // 장비명
  price: number | null; // 장비가액(원)
  powerKw: number | null; // 전력용량(kW)
  areaM2: number | null; // 설치면적(㎡)
  headcount: number; // 인원
}

export interface Process {
  key: string; // 공정명 (lookup 키)
  equipment: Equipment;
  powerLoadRatio: number | null; // 전력소비율 placeholder (가공비 이슈에서 결정)
}

export interface LaborRate {
  process: string;
  rate: number; // 원/hr
}

export interface MaterialDensity {
  series: string;
  density: number; // 비중
  gPerMm3: number; // 비중 / 1000 (g/㎣)
}

export interface CostConstants {
  powerUnitPrice: number; // 전력단가 (₩/kWh)
  auxEquipmentRatio: number; // 부대설비비율
  buildingCost: number; // 건축비용 (₩/㎡)
  buildingYears: number; // 건물년수
  equipmentYearsDedicated: number; // 설비년수(전용)
  equipmentYearsGeneral: number; // 설비년수(범용)
  repairRatio: number; // 수선비율
  dailyWorkHours: number; // 일작업시간 (h)
  yearlyWorkDays: number; // 년작업일수 (일)
  indirectExpenseRatio: number; // 간접경비율
}

const eq = (
  name: string,
  price: number | null,
  powerKw: number | null,
  areaM2: number | null,
  headcount: number
): Equipment => ({ name, price, powerKw, areaM2, headcount });

const proc = (
  key: string,
  equipment: Equipment,
  powerLoadRatio: number | null = null
): Process => ({
  key,
  equipment,
  powerLoadRatio,
});

// 공정 → 설비 (직접경비_내부!C22:I33)
export const PROCESSES: readonly Process[] = [
  proc("냉간단조(F)", eq("Forming/M", 450_000_000, 45, 25, 0.25)),
  proc("냉간단조(H)", eq("Heading/M", 200_000_000, 35, 25, 0.25)),
  proc("분할단조", eq("TR/M", 120_000_000, 35, 20, 0.25)),
  proc("홈가공", eq("홈가공/M", 18_000_000, 15, 35, 0.25)),
  proc("너링", eq("Knurling/M", 41_500_000, 9, 12, 0.2)),
  proc("나사산", eq("Tapping/M", 41_500_000, 9.5, 12, 0.2)),
  proc("전수검사", eq("Vision", 70_000_000, 2, 2, 0.2)),
  proc("가공(본동)", eq("CNC", 99_000_000, 15, 35, 0.25)),
  proc("가공(B동)", eq("CNC", 40_000_000, 15, 35, 0.25)),
  proc("연마", eq("연마기", 40_000_000, 20, 12, 0.25)),
  proc("최종검사/포장", eq("수작업", null, null, null, 1)),
];

// 공정명 → Equipment
export const EQUIPMENT: Record<string, Equipment> = Object.fromEntries(
  PROCESSES.map((p) => [p.key, p.equipment])
);

// 임율 (직접경비_내부!C35:D46).
// Excel 의 일반 '가공'·'CNC' 키를 설비 마스터의 공정명에 맞춰 정규화:
//   - 가공(본동) = CNC(본동) = 26,000  (출처: CNC1!K13; #1 의 25,900 은 오기 → 26,000 정정 (#3))
//   - 가공(B동)  = 일반 가공  = 26,000
export const LABOR_RATES: readonly LaborRate[] = [
  { process: "냉간단조(F)", rate: 26_000 },
  { process: "냉간단조(H)", rate: 26_000 },
  { process: "분할단조", rate: 26_000 },
  { process: "홈가공", rate: 26_000 },
  { process: "너링", rate: 26_000 },
  { process: "나사산", rate: 26_000 },
  { process: "가공(B동)", rate: 26_000 },
  { process: "가공(본동)", rate: 26_000 }, // CNC1!K13 정정: 25,900 → 26,000 (#3)
  { process: "전수검사", rate: 20_880 },
  { process: "연마", rate: 20_880 },
  { process: "최종검사/포장", rate: 20_880 },
];

// 비중 (CNC1!AH3:AJ15)
export const DENSITIES: readonly MaterialDensity[] = [
  { series: "C3604", density: 8.43, gPerMm3: 8.43 / 1000 },
  { series: "C2700", density: 8.43, gPerMm3: 8.43 / 1000 },
  { series: "C2680", density: 8.47, gPerMm3: 8.47 / 1000 },
  { series: "C2600", density: 8.53, gPerMm3: 8.53 / 1000 },
  { series: "SUS316", density: 7.98, gPerMm3: 7.98 / 1000 },
  { series: "SUS304", density: 7.93, gPerMm3: 7.93 / 1000 },
  { series: "SUS420J2", density: 7.75, gPerMm3: 7.75 / 1000 },
  { series: "STEEL", density: 7.85, gPerMm3: 7.85 / 1000 },
  { series: "AL", density: 2.699, gPerMm3: 2.699 / 1000 },
  { series: "SUS420F", density: 7.75, gPerMm3: 7.75 / 1000 },
  { series: "SUS303", density: 7.93, gPerMm3: 7.93 / 1000 },
  { series: "C1100H", density: 8.94, gPerMm3: 8.94 / 1000 },
  { series: "SUM24L", density: 7.87, gPerMm3: 7.87 / 1000 },
];

// 원가 상수 (CNC1!D56:M57)
export const CONSTANTS: CostConstants = {
  powerUnitPrice: 121,
  auxEquipmentRatio: 3.5,
  buildingCost: 290_000,
  buildingYears: 40,
  equipmentYearsDedicated: 6,
  equipmentYearsGeneral: 10,
  repairRatio: 0.12,
  dailyWorkHours: 20,
  yearlyWorkDays: 260,
  indirectExpenseRatio: 0.4,
};

const RATE_BY_PROCESS: Record<string, number> = Object.fromEntries(
  LABOR_RATES.map((r) => [r.process, r.rate])
);
const DENSITY_BY_SERIES: Record<string, MaterialDensity> = Object.fromEntries(
  DENSITIES.map((d) => [d.series, d])
);

// ----- lookup 헬퍼 (하위 이슈에서 import) -----

export function densityOf(series: string): number {
  const d = DENSITY_BY_SERIES[series];
  if (d === undefined) throw new Error(`Unknown material series: ${series}`);
  return d.density;
}

export function laborRateOf(process: string): number {
  const r = RATE_BY_PROCESS[process];
  if (r === undefined) throw new Error(`Unknown process for labor rate: ${process}`);
  return r;
}

export function equipmentOf(process: string): Equipment {
  const e = EQUIPMENT[process];
  if (e === undefined) throw new Error(`Unknown process for equipment: ${process}`);
  return e;
}

export function constants(): CostConstants {
  return CONSTANTS;
}
